import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const Setup2FA: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [step, setStep] = useState<'setup' | 'verify' | 'complete'>('setup');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.two_factor_enabled) {
      navigate('/dashboard');
      return;
    }
    initiate2FA();
  }, [user, navigate]);

  const initiate2FA = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/users/2fa/setup`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setQrCode(response.data.qr_code);
      setSecret(response.data.secret);
      setStep('setup');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to setup 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/users/2fa/enable`,
        { code: verificationCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setBackupCodes(response.data.backup_codes);
      setStep('complete');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

const handleComplete = async () => {
  // Refresh user data to show 2FA is enabled
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API_URL}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    // Force page reload to update AuthContext
    window.location.href = '/dashboard';
  } catch (err) {
    navigate('/dashboard');
  }
};

  if (loading && step === 'setup') {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <p>Setting up 2FA...</p>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#f5f7fa',
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        padding: '40px',
        borderRadius: '8px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        maxWidth: '500px',
        width: '100%'
      }}>
        {step === 'setup' && (
          <>
            <h2>Enable Two-Factor Authentication</h2>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
            </p>
            
            {qrCode && (
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <img src={qrCode} alt="QR Code" style={{ maxWidth: '250px' }} />
              </div>
            )}
            
            {secret && (
              <div style={{ 
                background: '#f8f9fa', 
                padding: '15px', 
                borderRadius: '4px',
                marginBottom: '20px',
                wordBreak: 'break-all'
              }}>
                <strong>Manual Entry Code:</strong><br />
                <code style={{ fontSize: '14px' }}>{secret}</code>
              </div>
            )}

            <button
              onClick={() => setStep('verify')}
              style={{
                width: '100%',
                padding: '12px',
                background: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '16px',
                cursor: 'pointer'
              }}
            >
              I've Scanned the QR Code
            </button>

            <button
              onClick={() => navigate('/dashboard')}
              style={{
                width: '100%',
                marginTop: '10px',
                padding: '12px',
                background: 'transparent',
                color: '#667eea',
                border: '1px solid #667eea',
                borderRadius: '4px',
                fontSize: '16px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </>
        )}

        {step === 'verify' && (
          <>
            <h2>Verify Your Code</h2>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              Enter the 6-digit code from your authenticator app
            </p>

            {error && (
              <div style={{ 
                background: '#f8d7da', 
                color: '#721c24', 
                padding: '12px', 
                borderRadius: '4px',
                marginBottom: '15px'
              }}>
                {error}
              </div>
            )}

            <form onSubmit={handleVerify}>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '24px',
                  textAlign: 'center',
                  letterSpacing: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  marginBottom: '15px'
                }}
                autoFocus
              />

              <button
                type="submit"
                disabled={verificationCode.length !== 6 || loading}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: verificationCode.length === 6 ? '#667eea' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '16px',
                  cursor: verificationCode.length === 6 ? 'pointer' : 'not-allowed'
                }}
              >
                {loading ? 'Verifying...' : 'Verify & Enable 2FA'}
              </button>

              <button
                type="button"
                onClick={() => setStep('setup')}
                style={{
                  width: '100%',
                  marginTop: '10px',
                  padding: '12px',
                  background: 'transparent',
                  color: '#667eea',
                  border: '1px solid #667eea',
                  borderRadius: '4px',
                  fontSize: '16px',
                  cursor: 'pointer'
                }}
              >
                Back
              </button>
            </form>
          </>
        )}

        {step === 'complete' && (
          <>
            <h2>✅ 2FA Enabled Successfully!</h2>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              Save these backup codes in a safe place. You can use them to access your account if you lose your authenticator device.
            </p>

            <div style={{
              background: '#f8f9fa',
              padding: '20px',
              borderRadius: '4px',
              marginBottom: '20px'
            }}>
              <h3 style={{ marginTop: 0 }}>Backup Codes:</h3>
              {backupCodes.map((code, index) => (
                <div key={index} style={{ 
                  padding: '8px',
                  fontFamily: 'monospace',
                  fontSize: '14px'
                }}>
                  {code}
                </div>
              ))}
            </div>

            <button
              onClick={handleComplete}
              style={{
                width: '100%',
                padding: '12px',
                background: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '16px',
                cursor: 'pointer'
              }}
            >
              Continue to Dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default Setup2FA;
